import { useId, useMemo, useState, type CSSProperties } from "react";
import type { MintFeeView } from "../types";
import { BoltIcon, InfoIcon } from "../../../components/Icons";
import { deriveMintProgress, formatBigInt, formatWeiAsEth } from "../../../components/format";
import { ELYSIUM, sameIdentity } from "../../../config/elysium";
import { Overlay } from "../../../components/overlays/Overlay";
import { formatEther } from "viem";
import "../styles/mint-fees.css";

const FEE_STEP_INCREMENT_WEI = 2_000_000_000_000_000n;

interface MintProgressBarProps {
  state: MintFeeView;
}

export function MintProgressBar({ state }: MintProgressBarProps) {
  return state.nativeSymbol === "HYPE" || sameIdentity(state.collectionAddress, ELYSIUM.collection)
    ? <TierFeeProgress state={state} /> : <LegacyFeeProgress state={state} />;
}

function TierFeeProgress({ state }: MintProgressBarProps) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const progress = useMemo(() => {
    if (!state.available || state.cumulativeTotalMinted === null || state.mintFeeStepSize !== 200) return null;
    try {
      const value = deriveMintProgress(state.cumulativeTotalMinted, 200);
      // The remaining count must belong to the same PoW fee-counter observation.
      return state.successfulMintsUntilNextFeeStep !== undefined &&
        BigInt(state.successfulMintsUntilNextFeeStep) === BigInt(value.mintsRemaining) ? value : null;
    } catch { return null; }
  }, [state.available, state.cumulativeTotalMinted, state.mintFeeStepSize, state.successfulMintsUntilNextFeeStep]);
  const symbol = state.nativeSymbol ?? "HYPE";
  const fee = (table: Record<string, string> | undefined, tier: number) =>
    state.available && table?.[String(tier)] !== undefined ? formatEther(BigInt(table[String(tier)]!)) : "—";
  return <>
    <section className="mint-progress-panel tier-fees" aria-labelledby={titleId} aria-busy={state.loading}>
      <div className="mint-total"><span className="field-eyebrow" id={titleId}>NFTs MINED</span>
        <strong>{state.available && state.powTotalMinted != null ? formatBigInt(BigInt(state.powTotalMinted)) : "—"}</strong>
        <span className="mint-total-note" title={`Successful PoW mints confirmed by the indexer${state.powTotalMintedAsOfBlock == null ? "" : ` through block #${state.powTotalMintedAsOfBlock}`}. May lag the chain; includes burned/redeemed NFTs.`}>Indexer-confirmed PoW mints <InfoIcon /></span></div>
      <div className="step-progress"><div className="step-progress-heading"><span>FEE STEP {progress ? formatBigInt(progress.stepIndex + 1n) : "—"}</span>
        <span className="mono">{progress ? `${progress.stepProgress} / 200` : "— / 200"}</span></div>
        <div className="pixel-progress-track" role="progressbar" aria-label="Mints in current fee step" aria-valuemin={0} aria-valuemax={200}
          aria-valuenow={progress?.stepProgress ?? 0} aria-valuetext={progress ? `${progress.stepProgress} of 200 successful PoW mints` : "Mint count unavailable"}>
          <span className="pixel-progress-fill" style={{ "--progress": `${progress ? progress.ratio * 100 : 0}%` } as CSSProperties} />
          <span className="pixel-progress-grid" aria-hidden="true" /></div>
        <p>{state.cacheStale ? "Stale display data — refresh before preparing a mint" : state.loading ? "Loading mint fees…" :
          progress ? `${state.successfulMintsUntilNextFeeStep} PoW mints until the next 2% increase` : state.reason ?? "Fee step unavailable"}</p></div>
      <div className="fee-readout current"><BoltIcon /><div><span>GLOBAL FEE INCREASE</span><strong>+2% <small>/ STEP</small></strong><span>Every 200 successful PoW mints</span></div></div>
      <div className="fee-readout next"><div><span>MINT FEES BY TIER</span><button type="button" className="secondary-action fees-trigger"
        aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>View fees ↗</button></div></div>
    </section>
    <Overlay open={open} onClose={() => setOpen(false)} title="Mint fees by tier" eyebrow="GLOBAL MINT FEES" className="mint-fees-modal">
      {state.cacheStale ? <p role="status">Last known fees · stale data. Refresh before preparing a mint.</p> : null}
      {!state.available || !state.tierMintFeesWei ? <p role="status">{state.loading ? "Loading mint fees…" : "Current tier fees unavailable."}</p> : null}
      {!state.nextStepTierMintFeesWei ? <p role="status">Next-step fees unavailable.</p> : null}
      <div className="mint-fees-table-wrap"><table className="mint-fees-table" aria-label="Mint fees by net buy tier"><thead><tr>
        <th>Net Buy tier</th><th>Current · {symbol}</th><th>Next step · {symbol}</th></tr></thead><tbody>
        {[10, 100, 500, 1000, 2000, 5000].map(tier => <tr key={tier}><th scope="row">${tier.toLocaleString("en-US")}</th>
          <td>{fee(state.tierMintFeesWei, tier)}</td><td>{fee(state.nextStepTierMintFeesWei, tier)}</td></tr>)}
      </tbody></table></div>
      <p>Task/referral mint fee: 0 {symbol} (Free). Network gas is separate.</p>
      <p>Next step is the next 2% increase, not necessarily the next NFT. Net Buy determines eligibility; the tier is not an NFT purchase price.</p>
    </Overlay>
  </>;
}

function LegacyFeeProgress({ state }: MintProgressBarProps) {
  const stepSize = state.mintFeeStepSize ?? 300;
  const progress = useMemo(() => {
    if (!state.available || state.cumulativeTotalMinted === null) return null;
    try { return deriveMintProgress(state.cumulativeTotalMinted, stepSize); }
    catch { return null; }
  }, [state.available, state.cumulativeTotalMinted, stepSize]);

  const progressPercent = progress ? progress.ratio * 100 : 0;
  const currentFee = state.available && state.currentFeeWei !== null ? formatWeiAsEth(state.currentFeeWei, 18) : null;
  const increment = FEE_STEP_INCREMENT_WEI;
  const nextFee = state.available && !state.cacheStale && state.currentFeeWei !== null
    ? formatWeiAsEth(BigInt(state.currentFeeWei) + increment, 18)
    : null;

  return (
    <section className="mint-progress-panel" aria-labelledby="nfts-mined-title" aria-busy={state.loading}>
      <div className="mint-total">
        <span className="field-eyebrow" id="nfts-mined-title">NFTs MINED</span>
        <strong>{progress ? formatBigInt(progress.total) : "—"}</strong>
        <span className="mint-total-note">Cumulative successful NFT mints <InfoIcon /></span>
      </div>
      <div className="step-progress">
        <div className="step-progress-heading">
          <span>FEE STEP {progress ? formatBigInt(progress.stepIndex + 1n) : "—"}</span>
          <span className="mono">{progress ? `${progress.stepProgress} / ${stepSize}` : `— / ${stepSize}`}</span>
        </div>
        <div
          className="pixel-progress-track"
          role="progressbar"
          aria-label="Mints in current fee step"
          aria-valuemin={0}
          aria-valuemax={stepSize}
          aria-valuenow={progress?.stepProgress ?? 0}
          aria-valuetext={progress ? `${progress.stepProgress} of ${stepSize} successful mints` : "Mint count unavailable"}
        >
          <span className="pixel-progress-fill" style={{ "--progress": `${progressPercent}%` } as CSSProperties} />
          <span className="pixel-progress-grid" aria-hidden="true" />
        </div>
        <p>{state.cacheStale ? "Stale display data — refresh before preparing a mint" : state.loading ? "Loading mint fee…" : progress ? `${progress.mintsRemaining} mints until next fee step` :
          state.reason === "CONTRACT_NOT_CONFIGURED" ? "CONTRACT NOT DEPLOYED" :
            state.reason === "BACKEND_UNAVAILABLE" ? "BACKEND UNAVAILABLE" :
            state.reason ?? "Waiting for confirmed collection state"}</p>
      </div>
      <div className="fee-readout current">
        <BoltIcon />
        <div><span>{state.cacheStale ? "LAST KNOWN MINT FEE" : "CURRENT MINT FEE"}</span><strong>{currentFee ?? "—"} <small>{state.nativeSymbol ?? "ETH"}</small></strong></div>
      </div>
      <div className="fee-readout next">
        <div><span>NEXT STEP FEE</span><strong>{nextFee ?? "—"} <small>{state.nativeSymbol ?? (sameIdentity(state.collectionAddress, ELYSIUM.collection) ? "HYPE" : "ETH")}</small></strong></div>
      </div>
    </section>
  );
}
